import { makeAutoObservable } from 'mobx';

export const BROWSER_VIEWING_TIMEOUT_MS = 120_000;

/** Tracks connected viewers from least to most recently selected */
export class BrowserViewerConnections {
  private readonly viewers = new Set<BrowserViewerStore>();

  retain(viewer: BrowserViewerStore): void {
    for (const connected of this.viewers) connected.tick();
    this.viewers.delete(viewer);
    this.viewers.add(viewer);
    if (this.viewers.size > 5) this.viewers.values().next().value?.pause();
  }

  release(viewer: BrowserViewerStore): void {
    this.viewers.delete(viewer);
  }
}

/** Owns this viewer's connection, never the retained provider browser */
export class BrowserViewerStore {
  available = false;
  isSelected = true;
  deadline: number | null = null;
  now = Date.now();
  private browserTurnActive = false;

  constructor(private readonly connections?: BrowserViewerConnections) {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  setSelected(isSelected: boolean): void {
    this.isSelected = isSelected;
    this.tick();
    if (isSelected && this.isViewing) this.connections?.retain(this);
  }

  get isViewing(): boolean {
    return (
      this.available &&
      this.deadline !== null &&
      (this.browserTurnActive || this.now < this.deadline)
    );
  }

  setAvailable(available: boolean): void {
    this.available = available;
    if (!available) this.pause();
    this.tick();
  }

  tick(): void {
    this.now = Date.now();
    if (this.deadline !== null && this.browserTurnActive) {
      this.deadline = this.now + BROWSER_VIEWING_TIMEOUT_MS;
    }
    if (this.deadline !== null && this.now >= this.deadline) this.pause();
  }

  resume(): void {
    this.tick();
    if (this.available) {
      this.connections?.retain(this);
      this.deadline = this.now + BROWSER_VIEWING_TIMEOUT_MS;
    }
  }

  extend(): void {
    this.tick();
    if (this.isViewing) this.deadline = this.now + BROWSER_VIEWING_TIMEOUT_MS;
  }

  setBrowserTurnActive(active: boolean): void {
    if (active === this.browserTurnActive) return;
    // Finish the active turn's deadline before releasing the hold
    this.tick();
    this.browserTurnActive = active;
    this.tick();
  }

  canConnect(): boolean {
    this.tick();
    return this.isViewing;
  }

  pause(): void {
    this.deadline = null;
    this.connections?.release(this);
  }
}
